import type { State } from './reducer'
import type { State as HomeState } from 'shared/store/homepage'
import type {
    ActionAPIError,
    ActionNetworkError,
    ActionResetNetworkError
} from 'shared/store/actions'
import { actionNetworkError } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import { errors } from 'shared/utils'
import type { Dispatch } from 'redux'
import type {
    Candidate,
    ElectionForm,
    OperationDevice,
    OperationRepeat
} from 'shared/types'
import type { CouponSubscribe } from 'shared/store/custom-operation'

const {
    wallet,
    drive,
    fetch,
    election
} = endpoints.pages.operation.democraticBurger

type ActionDemocraticBurgerCouponsPageDataResponse = {
    type: 'PAGE_DATA_DEMOCRATIC_BURGER_COUPONS_RESPONSE',
    data: OperationDevice
}

type ActionDemocraticBurgerElectionPageDataLoading = {
    type: 'PAGE_DATA_DEMOCRATIC_BURGER_ELECTION_LOADING',
    dataElectionLoading: boolean
}

type ActionDemocraticBurgerElectionPageDataResponse = {
    type: 'PAGE_DATA_DEMOCRATIC_BURGER_ELECTION_RESPONSE',
    data: State
}

type ActionDemocraticBurgerVoteSuccess = {
    type: 'DEMOCRATIC_BURGER_VOTE_SUCCESS',
    myVote: Candidate
}

type ActionDemocraticBurgerVoteError = {
    type: 'DEMOCRATIC_BURGER_VOTE_ERROR',
    voteError?: string
}

type ActionDemocraticBurgerPageError = {
    type: 'DEMOCRATIC_BURGER_PAGE_ERROR',
    error?: any
}

type ActionHomepageOperationRepeat = {
    type: 'PAGE_DATA_HOMEPAGE_RESPONSE',
    data: HomeState
}

type ActionDemocraticBurgerSetOperationRepeat = {
    type: 'DEMOCRATIC_BURGER_SET_OPERATION_REPEAT',
    operationRepeat: OperationRepeat
}

const setDemocraticBurgerOperationRepeat = (
    operationRepeat: OperationRepeat
): ActionDemocraticBurgerSetOperationRepeat => ({
    type: 'DEMOCRATIC_BURGER_SET_OPERATION_REPEAT',
    operationRepeat
})

const fetchDemocraticBurgerCouponsPageData = (
    subscribe: CouponSubscribe
) => async (dispatch: Dispatch<any>) => {
    try {
        let response = null
        const { restaurantCode, deviceCheckPayload } = subscribe
        const { hash, king } = deviceCheckPayload
        if (restaurantCode) {
            response = await ws.get(drive, {
                params: {
                    king,
                    hash,
                    restaurantCode
                }
            })
        } else {
            response = await ws.post(fetch, deviceCheckPayload)
        }
        dispatch(getDemocraticBurgerCouponsData(response.data))
    } catch (error) {
        console.error('Error getting democratic burger coupons data', error)
        manageError(error, dispatch)
    }
}

const getDemocraticBurgerCouponsData = (
    data: OperationDevice
): ActionDemocraticBurgerCouponsPageDataResponse => ({
    type: 'PAGE_DATA_DEMOCRATIC_BURGER_COUPONS_RESPONSE',
    data
})

const actionDemocraticBurgerElectionData = (
    data: State
): ActionDemocraticBurgerElectionPageDataResponse => ({
    type: 'PAGE_DATA_DEMOCRATIC_BURGER_ELECTION_RESPONSE',
    data
})

const fetchDemocraticBurgerElectionPageData = (king: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            dispatch(loadingElectionData(true))
            const response = await ws.get(election, {
                params: { king }
            })
            dispatch(actionDemocraticBurgerElectionData(response.data))
            return response.data
        } catch (error) {
            console.error('Error getting democratic burger election data')
            manageError(error, dispatch)
            dispatch(loadingElectionData(false))
            return false
        }
    }
}

const loadingElectionData = (
    dataElectionLoading: boolean
): ActionDemocraticBurgerElectionPageDataLoading => ({
    type: 'PAGE_DATA_DEMOCRATIC_BURGER_ELECTION_LOADING',
    dataElectionLoading
})

const actionError = (error: any): ActionDemocraticBurgerPageError => ({
    type: 'DEMOCRATIC_BURGER_PAGE_ERROR',
    error
})

const vote = (myVote: ElectionForm) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            dispatch(actionVoteError(undefined))
            const response = await ws.post(election, myVote)
            if (response) {
                dispatch(actionVoteSuccess(response.data))
                return true
            }
            throw new Error('Response data does not contain myVote field.')
        } catch (error) {
            console.error('Error, could not vote')
            const { response } = error
            console.error(
                'Error voting in democratic burger operation',
                response
            )
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.CANDIDATE_NOT_FOUND:
                            message =
                                'democratic.vote.form.candidate.not.found.error'
                            break
                        case errors.CANDIDATE_ALREADY_ELECTED:
                            message =
                                'democratic.vote.form.candidate.already.elected.error'
                            break
                        case errors.ALREADY_VOTED:
                            message = 'democratic.vote.form.already.voted.error'
                            break
                        case errors.NOT_ALLOWED:
                            message = 'democratic.vote.form.not.allowed.error'
                            break
                        case errors.ELECTION_CLOSED:
                            message = 'democratic.vote.form.closed.error'
                            break
                        case errors.NO_CAPTCHA_TOKEN_PROVIDED:
                            message = 'democratic.vote.form.no.captcha.error'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionVoteError(message))
            return false
        }
    }
}

const actionVoteSuccess = (
    myVote: Candidate
): ActionDemocraticBurgerVoteSuccess => ({
    type: 'DEMOCRATIC_BURGER_VOTE_SUCCESS',
    myVote
})

const actionVoteError = (
    voteError?: string
): ActionDemocraticBurgerVoteError => ({
    type: 'DEMOCRATIC_BURGER_VOTE_ERROR',
    voteError
})

const manageError = (error: any, dispatch: Dispatch<any>) => {
    console.error('error in democratic burger', error)
    switch (error) {
        case errors.NETWORK:
            dispatch(
                actionNetworkError({
                    type: errors.NETWORK
                })
            )
            break
        case errors.TIMEOUT:
            dispatch(
                actionNetworkError({
                    type: errors.TIMEOUT
                })
            )
            break
        default:
            dispatch(actionError(error))
            break
    }
}

const getWallet = (
    operationCode: string,
    restaurantCode: string,
    king: string
) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            dispatch(actionError(undefined))
            const response = await ws.get(wallet, {
                params: {
                    king,
                    restaurantCode: restaurantCode
                }
            })
            return response.data && response.data.wallet
        } catch (error) {
            console.error('Error, could not get coupon for wallet', error)
            manageError(error, dispatch)
            return false
        }
    }
}

export {
    actionError,
    getWallet,
    vote,
    fetchDemocraticBurgerCouponsPageData,
    fetchDemocraticBurgerElectionPageData,
    setDemocraticBurgerOperationRepeat
}

export type Action =
    | ActionDemocraticBurgerCouponsPageDataResponse
    | ActionDemocraticBurgerElectionPageDataLoading
    | ActionDemocraticBurgerElectionPageDataResponse
    | ActionHomepageOperationRepeat
    | ActionDemocraticBurgerVoteSuccess
    | ActionDemocraticBurgerVoteError
    | ActionDemocraticBurgerSetOperationRepeat
    | ActionAPIError
    | ActionNetworkError
    | ActionResetNetworkError
    | ActionDemocraticBurgerPageError
